/**
 * Vacation responder / out-of-office hooks — Vault-backed, account-scoped.
 * Reads + writes the per-account config (enabled flag + subject/body/window).
 * Mirrors useSpam.ts conventions.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';
import type { EmailVacationSettingsDto } from '@/lib/api';

const mb = queryKeys.email.mailbox;
const conn = queryKeys.email.connections;

export interface VacationSettingsResult {
  enabled: boolean;
  settings: EmailVacationSettingsDto;
}

export function useVacationSettings(accountId: string | undefined, enabled = true) {
  return useQuery<VacationSettingsResult>({
    queryKey: mb.vacationSettings(accountId ?? ''),
    queryFn: () => api.getEmailMailboxVacationSettings(accountId as string),
    enabled: enabled && !!accountId,
  });
}

export function useSetVacationSettings(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: {
      enabled?: boolean;
      subject?: string;
      body?: string;
      startAt?: string | null;
      endAt?: string | null;
    }) => api.setEmailMailboxVacationSettings(accountId, patch),
    onSuccess: () => {
      toast({ description: 'Vacation responder saved' });
      qc.invalidateQueries({ queryKey: mb.vacationSettings(accountId) });
      qc.invalidateQueries({ queryKey: conn.all });
    },
    onError: (e) =>
      toast({
        description: e instanceof Error && e.message ? e.message : 'Could not save settings',
        variant: 'destructive',
      }),
  });
}
